// src/components/Admin/PhoneNumbers.tsx
/// <reference types="@types/google.maps" />
import React, { useState, useEffect } from 'react';
import errorLogger from '../../services/errorLogger.service';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Grid,
  Tooltip,
  FormControlLabel,
  SelectChangeEvent,
  Radio,
  RadioGroup,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import GooglePlacesAutocomplete from '../Common/GooglePlacesAutocompleteNative';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { useNavigate } from 'react-router-dom';
import ChargePreviewDialog from '../Common/ChargePreviewDialog';
import { getCustomerParams as getCustomerParamsUtil, createUrlWithCustomerParams } from '../../utils/customerParams';

// Create component logger
const logger = errorLogger.createComponentLogger('PhoneNumbers');

interface PhoneNumber {
  did: string;
  type: number;
  active: number;
  custid: number;
  call_limit: number;
  pbxapp: number;
  pbxdesc: string;
  pbxdigits: string;
  recordid: number;
  CallerIDname: string;
  shared: number;
  exten?: string;
  TimeCondition: number;
  e911_address?: string;
  has_e911?: boolean;
  app_name?: string;
  forward_to?: string;
  cancellation_date?: string;
  recordincoming?: number;
  available_for_all?: boolean;
  shared_extensions?: string[];
}

interface Application {
  app_id: number;
  app_name: string;
}

interface Extension {
  MyExten: string;
  fullname: string;
}

interface VoicemailBox {
  mailbox: string;
  fullname: string;
  MyExten: string;
}

interface ExtensionGroup {
  GroupNumber: string;
  GroupName: string;
}

interface IVR {
  ivrID: number;
  Description: string;
}

interface CallQueue {
  extension: string;
  name: string;
}

interface ConsecutiveDial {
  ConsecID: string;
  ConsecName: string;
}

interface Recording {
  RecordID: number;
  Name: string;
  Notes: string;
}

export default function PhoneNumbers() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();
  const customerFilter = useCustomerFilter();
  const navigate = useNavigate();
  
  // Check for login token on initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = !!sessionStorage.getItem('loginAsCustomer');
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.info('Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });
  
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [extensionGroups, setExtensionGroups] = useState<ExtensionGroup[]>([]);
  const [ivrs, setIvrs] = useState<IVR[]>([]);
  const [queues, setQueues] = useState<CallQueue[]>([]);
  const [consecutiveDials, setConsecutiveDials] = useState<ConsecutiveDial[]>([]);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [voicemails, setVoicemails] = useState<VoicemailBox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState<PhoneNumber | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [editCallLimit, setEditCallLimit] = useState(false);
  const [numberTypeDialog, setNumberTypeDialog] = useState(false);
  const [availableExtensions, setAvailableExtensions] = useState<Extension[]>([]);
  const [selectedExtension, setSelectedExtension] = useState('');
  const [isPilotNumber, setIsPilotNumber] = useState(false);
  const [confirmDeleteShared, setConfirmDeleteShared] = useState(false);
  const [numberTypeSelection, setNumberTypeSelection] = useState('');
  const [sharedExtensions, setSharedExtensions] = useState<string[]>([]);
  const [availableForAll, setAvailableForAll] = useState(false);
  const [confirmNotForAll, setConfirmNotForAll] = useState(false);
  const [confirmMakeForAll, setConfirmMakeForAll] = useState(false);
  const [pendingExtension, setPendingExtension] = useState('');
  const [extensionUsers, setExtensionUsers] = useState<Record<string, {has_user: boolean, has_shared_access: boolean}>>({});
  const [selectedTab, setSelectedTab] = useState<'manual' | 'local' | 'tollfree' | 'international'>('manual');
  const [localCity, setLocalCity] = useState('');
  const [localArea, setLocalArea] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState<any[]>([]);
  const [selectedLocalNumber, setSelectedLocalNumber] = useState('');
  const [localNumberType, setLocalNumberType] = useState<'application' | 'direct' | 'shared'>('application');
  const [localExtension, setLocalExtension] = useState('');
  const [localSharedExtensions, setLocalSharedExtensions] = useState<string[]>([]);
  const [localAvailableForAll, setLocalAvailableForAll] = useState(false);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [orderingNumber, setOrderingNumber] = useState(false);
  const [cities, setCities] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [chargePreviewOpen, setChargePreviewOpen] = useState(false);
  const [chargeDetails, setChargeDetails] = useState<any>(null);
  const [chargePreviewLoading, setChargePreviewLoading] = useState(false);
  const [chargePreviewError, setChargePreviewError] = useState<string | null>(null);
  const [cancellationInfoDialog, setCancellationInfoDialog] = useState<{
    open: boolean;
    phoneNumber?: string;
    cancellationDate?: string;
  }>({ open: false });
  
  // Form states
  const [formData, setFormData] = useState({
    did: '',
    CallerIDname: '',
    call_limit: 10,
    pbxapp: 1,
    pbxdigits: '',
    pbxdesc: '',
    recordid: 0,
    recordincoming: 0,
    shared: false,
    exten: '',
    e911_address: '',
    e911_firstname: '',
    e911_lastname: '',
    e911_street_number: '',
    e911_street_name: '',
    e911_address_type: '',
    e911_unit: '',
    e911_city: '',
    e911_state: '',
    e911_zip: '',
    e911_other: '',
  });
  
  // E911 states
  const [e911Option, setE911Option] = useState<'pilot' | 'new' | 'keep'>('pilot');
  const [usePilotAddress, setUsePilotAddress] = useState(true);
  const [pilotNumber, setPilotNumber] = useState<any>(null);
  const [pilotE911Info, setPilotE911Info] = useState<any>(null);
  const [e911AddressConfirmed, setE911AddressConfirmed] = useState(false);
  const [currentE911Address, setCurrentE911Address] = useState<string>('');

  const canAddManual = member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin';
  
  // Helper to get customer filter params for API calls
  const getCustomerParams = () => {
    return getCustomerParamsUtil({
      isLoginAsCustomer,
      memberCustomerID: member?.customerID,
      customerFilterIsFiltered: customerFilter.isFiltered,
      customerFilterCustomerId: customerFilter.customerId,
      memberType: member?.memberType
    });
  };
  
  // Helper to build query params with customer ID for phone number endpoints
  const buildPhoneNumberQueryParams = (action?: string, additionalParams?: Record<string, string>) => {
    const params = new URLSearchParams();
    
    if (action) {
      params.set('action', action);
    }
    
    // Add customer ID
    if (isLoginAsCustomer && member?.customerID) {
      params.set('customerId', member.customerID.toString());
    } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
      params.set('customerId', customerFilter.customerId!);
    }
    
    // Add any additional params
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        params.set(key, value);
      });
    }
    
    return params.toString();
  };
  
  useEffect(() => {
    if (!member) return;
    
    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    
    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      logger.debug('Debug message');
      return;
    }
    
    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          logger.debug('Debug message');
          return;
        }
      } catch (e) {
        logger.error('Error occurred', e);
      }
    }
    
    // Add small delay to let state settle
    const timer = setTimeout(() => {
      fetchData();
    }, 200);
    
    return () => clearTimeout(timer);
  }, [member?.customerID, isLoginAsCustomer]);

  // Listen for login-as-customer changes to update tab title
  useEffect(() => {
    const handleLoginAsChange = (event: CustomEvent) => {
      if (event.detail && event.detail.customerId && event.detail.customerName) {
        document.title = `${event.detail.customerId} - ${event.detail.customerName} - Phone Numbers`;
      }
    };

    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
    
    // Also refetch data when login-as changes
    window.addEventListener('loginAsCustomerChanged', () => {
      setTimeout(() => {
        if (member) fetchData();
      }, 100);
    });

    // Set initial title based on mode
    if (isLoginAsCustomer && member) {
      const customerId = (member as any).customerID || (member as any).customer_id;
      const customerName = (member as any).loginAsCustomerName || '';
      if (customerId && customerName) {
        document.title = `${customerId} - ${customerName} - Phone Numbers`;
      }
    } else if (customerFilter.isFiltered && customerFilter.customerId && customerFilter.customerName) {
      const decodedCustomerName = decodeURIComponent(customerFilter.customerName);
      document.title = `${customerFilter.customerId} - ${decodedCustomerName} - Phone Numbers`;
    } else {
      document.title = 'Phone Numbers - Portal';
    }

    return () => {
      window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
      // Reset title when component unmounts
      if (!isLoginAsCustomer) {
        document.title = 'Portal';
      }
    };
  }, [customerFilter, isLoginAsCustomer, member]);

  // Auto-select first item in secondary dropdowns when application changes and no target is selected
  useEffect(() => {
    // Skip if Direct number
    if (!formData.shared && formData.exten) return;

    const { pbxapp, pbxdigits } = formData;

    // Auto-select based on application type
    if (pbxapp === 1 && extensionGroups.length > 0) {
      // Extension Group - only set if empty or invalid
      if (!pbxdigits || !extensionGroups.find(g => g.GroupNumber === pbxdigits)) {
        setFormData(prev => ({
          ...prev,
          pbxdigits: extensionGroups[0].GroupNumber,
          pbxdesc: extensionGroups[0].GroupName
        }));
      }
    } else if (pbxapp === 4 && extensions.length > 0) {
      // Single Extension - only set if empty or invalid
      if (!pbxdigits || !extensions.find(e => e.MyExten === pbxdigits)) {
        setFormData(prev => ({
          ...prev,
          pbxdigits: extensions[0].MyExten,
          pbxdesc: ''
        }));
      }
    } else if ((pbxapp === 7 || pbxapp === 17) && voicemails.length > 0) {
      // Voicemail - only set if empty or invalid
      if (!pbxdigits || !voicemails.find(v => v.mailbox === pbxdigits)) {
        setFormData(prev => ({
          ...prev,
          pbxdigits: voicemails[0].mailbox,
          pbxdesc: voicemails[0].fullname || ''
        }));
      }
    } else if (pbxapp === 16 && ivrs.length > 0) {
      // IVR - only set if empty or invalid
      if (!pbxdigits || !ivrs.find(i => i.Description === pbxdigits)) {
        setFormData(prev => ({
          ...prev,
          pbxdigits: ivrs[0].Description,
          pbxdesc: ivrs[0].Description
        }));
      }
    } else if (pbxapp === 3 && queues.length > 0) {
      // Call Queue - only set if empty or invalid
      if (!pbxdigits || !queues.find(q => q.extension === pbxdigits)) {
        setFormData(prev => ({
          ...prev,
          pbxdigits: queues[0].extension,
          pbxdesc: queues[0].name
        }));
      }
    } else if (pbxapp === 20 && consecutiveDials.length > 0) {
      // Consecutive Dial - only set if empty or invalid
      if (!pbxdigits || !consecutiveDials.find(d => d.ConsecID === pbxdigits)) {
        setFormData(prev => ({
          ...prev,
          pbxdigits: consecutiveDials[0].ConsecID,
          pbxdesc: consecutiveDials[0].ConsecID === '16' ? 'IATS-Incoming' : consecutiveDials[0].ConsecName
        }));
      }
    }
  }, [formData.pbxapp, formData.pbxdigits, formData.shared, formData.exten, extensionGroups, extensions, voicemails, ivrs, queues, consecutiveDials]);

  const fetchData = async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Debug message');
      return;
    }
    
    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          logger.error('Error occurred', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          setError('Customer context mismatch - please refresh the page');
          setLoading(false);
          return;
        }
      } catch (e) {
        logger.error('Error occurred', e);
      }
    }
    
    try {
      setLoading(true);
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      
      // Build query params with customer filter for master admins
      const queryParams = new URLSearchParams();
      queryParams.set('_t', timestamp.toString());
      
      if (isLoginAsCustomer && member?.customerID) {
        // When in "login as" mode, always use the overridden customer ID
        logger.debug('Have login token but no session data - waiting...', member.customerID);
        queryParams.set('customerId', member.customerID.toString());
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        logger.debug('Have login token but no session data - waiting...', customerFilter.customerId);
        queryParams.set('customerId', customerFilter.customerId!);
      } else {
        logger.debug('Debug message');
      }
      
      const baseQuery = queryParams.toString();
      
      const [
        numbersRes, 
        appsRes, 
        extRes,
        extGroupsRes,
        ivrsRes,
        queuesRes,
        dialRes,
        recRes,
        vmRes
      ] = await Promise.all([
        api.get(`/endpoints/phone_numbers?${baseQuery}`),
        api.get(`/endpoints/phone_numbers?action=applications&${baseQuery}`),
        api.get(`/endpoints/phone_numbers?action=extensions&${baseQuery}`),
        api.get(`/endpoints/phone_numbers?action=extension_groups&${baseQuery}`),
        api.get(`/endpoints/phone_numbers?action=ivr_menus&${baseQuery}`),
        api.get(`/endpoints/phone_numbers?action=call_queues&${baseQuery}`),
        api.get(`/endpoints/phone_numbers?action=consecutive_dials&${baseQuery}`),
        api.get(`/endpoints/phone_numbers?action=recordings&${baseQuery}`),
        api.get(`/endpoints/phone_numbers?action=voicemail_boxes&${baseQuery}`),
      ]);
      
      
      setPhoneNumbers(numbersRes.data.numbers || []);
      setApplications(appsRes.data.applications || []);
      setExtensions(extRes.data.extensions || []);
      setExtensionGroups(extGroupsRes.data.extension_groups || []);
      setIvrs(ivrsRes.data.ivr_menus || []);
      setQueues((queuesRes.data.call_queues || []).filter((q: CallQueue) => q.extension && q.name));
      setConsecutiveDials(dialRes.data.consecutive_dials || []);
      setRecordings(recRes.data.recordings || []);
      setVoicemails(vmRes.data.voicemail_boxes || []);
    } catch (err: any) {
      logger.error('Error occurred', err);
      logger.error('Error occurred', err.response);
      logger.error('Error occurred', err.response?.data);
      setError(err.response?.data?.message || err.message || 'Failed to load phone numbers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (number?: PhoneNumber) => {
    // Set default tab based on permissions when adding new number
    if (!number) {
      const defaultTab = canAddManual ? 'manual' : 'local';
      setSelectedTab(defaultTab);

      // If opening on local tab, fetch cities
      if (defaultTab === 'local') {
        fetchCities();
      }
      
      // Fetch available extensions for add dialog
      fetchAvailableExtensions();
    }
    
    // Fetch pilot number and E911 info with correct customer ID
    try {
      // Build query params with customer ID
      const pilotParams = new URLSearchParams();
      pilotParams.set('action', 'pilot_info');
      
      if (isLoginAsCustomer && member?.customerID) {
        pilotParams.set('customerId', member.customerID.toString());
        logger.debug('Have login token but no session data - waiting...', member.customerID);
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        pilotParams.set('customerId', customerFilter.customerId!);
        logger.debug('Have login token but no session data - waiting...', customerFilter.customerId);
      }
      
      const pilotResponse = await api.get(`/endpoints/phone_numbers?${pilotParams.toString()}`);
      if (pilotResponse.data.success) {
        setPilotNumber(pilotResponse.data.pilot);
        setPilotE911Info(pilotResponse.data.pilot_e911);
        
        // For new numbers, default to using pilot address if available
        if (!number && pilotResponse.data.pilot_e911) {
          setUsePilotAddress(true);
        }
      }
    } catch (err) {
      logger.error('Error occurred', err);
    }
    
    if (number) {
      setEditMode(true);
      setSelectedNumber(number);
      
      // Parse E911 address if available
      let e911Data = {
        e911_address: '',
        e911_firstname: '',
        e911_lastname: '',
        e911_street_number: '',
        e911_street_name: '',
        e911_address_type: '',
        e911_unit: '',
        e911_city: '',
        e911_state: '',
        e911_zip: '',
        e911_other: '',
      };
      
      let hasE911 = false;
      
      // If we need to fetch E911 data separately, do it here
      if (number.did) {
        try {
          // Build query params with customer ID
          const e911Params = new URLSearchParams();
          e911Params.set('action', 'e911_details');
          e911Params.set('did', number.did);
          
          if (isLoginAsCustomer && member?.customerID) {
            e911Params.set('customerId', member.customerID.toString());
          } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
            e911Params.set('customerId', customerFilter.customerId!);
          }
          
          const e911Response = await api.get(`/endpoints/phone_numbers?${e911Params.toString()}`);
          if (e911Response.data.success && e911Response.data.e911) {
            const e911 = e911Response.data.e911;
            hasE911 = true;
            
            // Format the current E911 address for display
            const addressParts = [];
            if (e911.street_number) addressParts.push(e911.street_number);
            if (e911.street_name) addressParts.push(e911.street_name);
            if (e911.city) addressParts.push(e911.city);
            if (e911.state) addressParts.push(e911.state);
            if (e911.postal_code) addressParts.push(e911.postal_code);
            setCurrentE911Address(addressParts.join(', '));
            
            e911Data = {
              e911_address: number.e911_address || '',
              e911_firstname: e911.FirstName || '',
              e911_lastname: e911.LastName || '',
              e911_street_number: e911.street_number || '',
              e911_street_name: e911.street_name || '',
              e911_address_type: e911.address_type || '',
              e911_unit: e911.address_type_number || '',
              e911_city: e911.city || '',
              e911_state: e911.state || '',
              e911_zip: e911.postal_code || '',
              e911_other: e911.OTHER_ADDRESS_INFO || '',
            };
          }
        } catch (err) {
          logger.error('Error occurred', err);
        }
      }
      
      // Set the appropriate E911 option
      if (hasE911) {
        setE911Option('keep');
        setUsePilotAddress(false);
      } else if (pilotE911Info) {
        setE911Option('pilot');
        setUsePilotAddress(true);
      } else {
        setE911Option('new');
        setUsePilotAddress(false);
      }
      
      // Debug logging
      logger.debug('Have login token but no session data - waiting...', {
        did: number.did,
        shared: number.shared,
        exten: number.exten,
        pbxapp: number.pbxapp,
        sharedWillBe: number.shared === 1
      });
      
      // Debug logging to see what we're getting
      logger.debug('Have login token but no session data - waiting...', {
        did: number.did,
        shared_raw: number.shared,
        shared_type: typeof number.shared,
        shared_converted: number.shared == 1,
        exten: number.exten
      });
      
      setFormData({
        did: number.did,
        CallerIDname: number.CallerIDname || '',
        call_limit: number.call_limit,
        pbxapp: number.pbxapp,
        pbxdigits: number.pbxdigits || '',
        pbxdesc: number.pbxdesc || '',
        recordid: number.recordid || 0,
        recordincoming: number.recordincoming || 0,
        shared: number.shared == 1,  // Use == to handle both string "1" and integer 1
        exten: number.exten || '',
        ...e911Data
      });
      
      // Fetch shared extensions info if it's a shared number
      if (number.shared == 1 && number.did) {  // Use == to handle both string "1" and integer 1
        fetchSharedExtensions(number.did);
      }
    } else {
      setEditMode(false);
      setSelectedNumber(null);
      // Don't set usePilotAddress here - it will be set after pilot info is fetched
      setE911AddressConfirmed(false);
      setFormData({
        did: '',
        CallerIDname: '',
        call_limit: 10,
        pbxapp: 1,  // Default to Extension Group instead of 0
        pbxdigits: '',
        pbxdesc: '',
        recordid: 0,
        recordincoming: 0,
        shared: false,
        exten: '',
        e911_address: '',
        e911_firstname: '',
        e911_lastname: '',
        e911_street_number: '',
        e911_street_name: '',
        e911_address_type: '',
        e911_unit: '',
        e911_city: '',
        e911_state: '',
        e911_zip: '',
        e911_other: '',
      });
    }
    setEditCallLimit(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedNumber(null);
    // Reset local tab fields
    setLocalCity('');
    setLocalArea('');
    setAvailableNumbers([]);
    setSelectedLocalNumber('');
    setLocalNumberType('direct');
    setLocalExtension('');
    setCities([]);
    setAreas([]);
  };

  const handleSubmit = async () => {
    try {
      // Validate that secondary dropdown has a value for applications that require it
      // Skip validation for Direct numbers (formData.shared === false && formData.exten !== '')
      if (formData.shared || !formData.exten) {
        const requiresTarget = [1, 3, 4, 7, 16, 17, 20]; // Extension Group, Call Queue, Single Extension, Voicemail, IVR, Voicemail (Busy), Consecutive Dial
        if (requiresTarget.includes(formData.pbxapp) && !formData.pbxdigits) {
          setSnackbar({
            open: true,
            message: 'Please select a target for the selected application',
            severity: 'error'
          });
          return;
        }
      }

      let e911Data;

      if (e911Option === 'pilot') {
        // Use pilot address - send use_pilot flag to copy from pilot
        e911Data = {
          firstname: '',
          lastname: '',
          street_number: '',
          street_name: '',
          address_type: '',
          address_type_number: '',
          city: '',
          state: '',
          postal_code: '',
          other: '',
          use_pilot: true
        };
      } else if (e911Option === 'keep') {
        // Keep current address - send the existing E911 data so backend can save/push to N911
        // When editing, formData should have the current E911 info populated
        e911Data = {
          firstname: formData.e911_firstname || '',
          lastname: formData.e911_lastname || '',
          street_number: formData.e911_street_number,
          street_name: formData.e911_street_name,
          address_type: formData.e911_address_type,
          address_type_number: formData.e911_unit,
          city: formData.e911_city,
          state: formData.e911_state,
          postal_code: formData.e911_zip,
          other: formData.e911_other || '',
          use_pilot: false
        };
      } else {
        // Enter new address
        e911Data = {
          firstname: formData.e911_firstname || '',
          lastname: formData.e911_lastname || '',
          street_number: formData.e911_street_number,
          street_name: formData.e911_street_name,
          address_type: formData.e911_address_type,
          address_type_number: formData.e911_unit,
          city: formData.e911_city,
          state: formData.e911_state,
          postal_code: formData.e911_zip,
          other: formData.e911_other || '',
          use_pilot: false
        };
      }
      
      const submitData = {
        ...formData,
        available_for_all: formData.shared ? availableForAll : false,
        shared_extensions: formData.shared && !availableForAll ? sharedExtensions : [],
        e911: e911Data
      };
      
      // Debug logging
      logger.debug('Have login token but no session data - waiting...', {
        shared: formData.shared,
        exten: formData.exten,
        available_for_all: submitData.available_for_all,
        shared_extensions: submitData.shared_extensions,
        full_data: submitData
      });
      
      // Remove CallerIDname from submitData when editing since it's read-only
      if (editMode) {
        const { CallerIDname, ...editData } = submitData;
        logger.debug('Have login token but no session data - waiting...', editData);
        const params = getCustomerParams();
        const updateUrl = createUrlWithCustomerParams(`/endpoints/phone_numbers/${selectedNumber?.did}`, params);
        await api.put(updateUrl, editData);
        setSnackbar({ open: true, message: 'Phone number updated successfully', severity: 'success' });
      } else {
        const params = getCustomerParams();
        const createUrl = createUrlWithCustomerParams('/endpoints/phone_numbers', params);
        await api.post(createUrl, submitData);
        setSnackbar({ open: true, message: 'Phone number added successfully', severity: 'success' });
      }
      handleCloseDialog();
      // Add a small delay to ensure database transaction is complete
      setTimeout(() => {
        fetchData();
      }, 500);
    } catch (err: any) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Operation failed', 
        severity: 'error' 
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedNumber) return;
    
    try {
      const params = getCustomerParams();
      const deleteUrl = createUrlWithCustomerParams(`/endpoints/phone_numbers/${selectedNumber.did}`, params);
      const response = await api.delete(deleteUrl);
      logger.debug('Have login token but no session data - waiting...', response.data);
      const cancellationDate = response.data.cancellation_date;
      
      // Show a more prominent dialog with the cancellation information
      if (cancellationDate) {
        // Parse the date as noon to avoid timezone issues
        const [year, month, day] = cancellationDate.split('-').map((num: string) => parseInt(num));
        const dateObj = new Date(year, month - 1, day, 12, 0, 0);
        const formattedDate = dateObj.toLocaleDateString('en-US', { 
          month: 'long', 
          day: 'numeric', 
          year: 'numeric' 
        });
        
        // Set a special dialog to show cancellation details
        setCancellationInfoDialog({
          open: true,
          phoneNumber: selectedNumber.did,
          cancellationDate: formattedDate
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: 'Phone number cancellation scheduled successfully.', 
          severity: 'success' 
        });
      }
      
      setDeleteDialog(false);
      setSelectedNumber(null);
      fetchData();
    } catch (err: any) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Cancellation failed', 
        severity: 'error' 
      });
    }
  };

  const handleReverseCancellation = async () => {
    if (!selectedNumber) return;
    
    try {
      const params = getCustomerParams();
      const reverseUrl = createUrlWithCustomerParams(`/endpoints/phone_numbers/${selectedNumber.did}/reverse-cancellation`, params);
      await api.post(reverseUrl);
      setSnackbar({ 
        open: true, 
        message: 'Cancellation has been reversed. The phone number will remain active.', 
        severity: 'success' 
      });
      handleCloseDialog();
      fetchData();
    } catch (err: any) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to reverse cancellation', 
        severity: 'error' 
      });
    }
  };

  const getNumberTypeDisplay = () => {
    if (formData.shared) {
      if (availableForAll) {
        return 'Shared for Outgoing Calls -> Available for all Extensions';
      } else if (sharedExtensions.length > 0) {
        // Show all extensions if less than 10, otherwise show count
        if (sharedExtensions.length < 10) {
          return `Shared for Outgoing Calls -> ${sharedExtensions.join(', ')}`;
        } else {
          return `Shared for Outgoing Calls -> ${sharedExtensions.length} extensions`;
        }
      }
      return 'Shared for Outgoing Calls';
    }
    if (!formData.shared && !formData.exten) return 'Application only';
    return `Direct -> ${formData.exten}`;
  };

  const fetchAvailableExtensions = async () => {
    try {
      const queryString = buildPhoneNumberQueryParams('available_extensions');
      const response = await api.get(`/endpoints/phone_numbers?${queryString}`);
      setAvailableExtensions(response.data.extensions || []);
    } catch (err) {
      logger.error('Error occurred', err);
    }
  };

  const fetchSharedExtensions = async (did: string) => {
    try {
      const queryString = buildPhoneNumberQueryParams('shared_extensions', { did });
      const response = await api.get(`/endpoints/phone_numbers?${queryString}`);
      setSharedExtensions(response.data.extensions || []);
      setAvailableForAll(response.data.available_for_all || false);
      setExtensionUsers(response.data.extension_users || {});
    } catch (err) {
      logger.error('Error occurred', err);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await api.get('/endpoints/phone_numbers?action=get_cities');
      const citiesData = response.data.cities || [];
      
      // Extract unique city names
      const uniqueCities = Array.from(new Set(
        citiesData.map((city: any) => typeof city === 'string' ? city : city.rc)
      )).sort();
      
      setCities(uniqueCities);
      return uniqueCities;
    } catch (err) {
      logger.error('Error occurred', err);
      setSnackbar({ open: true, message: 'Failed to fetch cities', severity: 'error' });
      return [];
    }
  };

  const fetchAreas = async (city: string) => {
    try {
      const response = await api.get(`/endpoints/phone_numbers?action=get_areas&city=${encodeURIComponent(city)}`);
      const areasData = response.data.areas || [];
      setAreas(areasData);
      return areasData;
    } catch (err) {
      logger.error('Error occurred', err);
      setSnackbar({ open: true, message: 'Failed to fetch areas', severity: 'error' });
      return [];
    }
  };

  const searchAvailableNumbers = async (npa: string, nxx: string) => {
    setSearchingNumbers(true);
    setAvailableNumbers([]);
    try {
      logger.info('Searching for numbers', {
        npa,
        nxx,
        hasToken: !!localStorage.getItem('access_token'),
        tokenLength: localStorage.getItem('access_token')?.length || 0
      });

      logger.info('About to make search API call');
      const response = await api.get(`/endpoints/phone_numbers?action=search_local_numbers&npa=${npa}&nxx=${nxx}&limit=10`);

      logger.info('Search API call completed', {
        status: response?.status,
        hasData: !!response?.data,
        dataType: typeof response?.data
      });

      logger.info('Search numbers response received', {
        success: response.data?.success,
        count: response.data?.numbers?.length || 0,
        numbers: response.data?.numbers
      });
      setAvailableNumbers(response.data.numbers || []);
    } catch (err: any) {
      logger.error('Search numbers failed - CATCH BLOCK', {
        errorType: err?.constructor?.name,
        status: err.response?.status,
        message: err.message,
        stack: err.stack?.substring(0, 500),
        data: err.response?.data,
        hasTokenAfterError: !!localStorage.getItem('access_token')
      });
      // Check if this is causing logout
      if (err.response?.status === 401 || err.message?.includes('No refresh token')) {
        logger.error('Auth failure detected in search_local_numbers - NOT logging out to debug', {
          status: err.response?.status,
          message: err.message,
          url: '/endpoints/phone_numbers?action=search_local_numbers'
        });
        // Don't automatically clear auth here - let user try again
        // setSnackbar({ open: true, message: 'Authentication error. Please try logging in again.', severity: 'error' });
      }
      setSnackbar({ open: true, message: 'Failed to search numbers. Please try again.', severity: 'error' });
    } finally {
      setSearchingNumbers(false);
    }
  };

  const checkPilotNumber = async (extension: string) => {
    try {
      const queryString = buildPhoneNumberQueryParams('check_pilot', { extension });
      const response = await api.get(`/endpoints/phone_numbers?${queryString}`);
      setIsPilotNumber(response.data.is_pilot || false);
    } catch (err) {
      logger.error('Error occurred', err);
    }
  };

  const handleNumberTypeOpen = () => {
    setSelectedExtension(formData.exten || '');
    setIsPilotNumber(false);
    setNumberTypeSelection(formData.shared ? 'shared' : (!formData.exten ? 'application' : 'direct'));
    // Don't reset sharedExtensions if we're already in shared mode - preserve the local state
    if (!formData.shared) {
      setSharedExtensions([]);
    }
    // Don't reset availableForAll here - let fetchSharedExtensions set it from database
    // Don't reset extensionUsers - let fetchSharedExtensions populate it
    fetchAvailableExtensions();
    if (formData.did) {
      // Always fetch shared extensions data to get the extension_users info
      fetchSharedExtensions(formData.did);
    }
    setNumberTypeDialog(true);
  };

  const handleSharedExtensionToggle = (extension: string, checked: boolean) => {
    // If available for all is checked and user is unchecking an extension
    if (availableForAll && !checked) {
      setPendingExtension(extension);
      setConfirmNotForAll(true);
      return;
    }
    
    // Update the shared extensions
    let newSharedExtensions: string[];
    if (checked) {
      newSharedExtensions = [...sharedExtensions, extension];
    } else {
      newSharedExtensions = sharedExtensions.filter(ex => ex !== extension);
    }
    setSharedExtensions(newSharedExtensions);
    
    // Check if all extensions with users are now selected
    const extensionsWithUsers = extensions.filter(ext => 
      extensionUsers[ext.MyExten]?.has_user
    );
    if (checked && newSharedExtensions.length === extensionsWithUsers.length) {
      setConfirmMakeForAll(true);
    }
  };

  const handleNumberTypeChange = async () => {
    const currentType = formData.shared ? 'shared' : (!formData.exten ? 'application' : 'direct');
    
    // Check if direct is selected but no extension chosen
    if (numberTypeSelection === 'direct' && !selectedExtension) {
      setSnackbar({ 
        open: true, 
        message: 'Please select an extension for the direct number', 
        severity: 'error' 
      });
      return;
    }
    
    // Check if we're changing from shared to direct
    if (currentType === 'shared' && numberTypeSelection === 'direct' && selectedExtension) {
      setConfirmDeleteShared(true);
      return;
    }
    
    // Check if direct is selected but has pilot number
    if (numberTypeSelection === 'direct' && selectedExtension && isPilotNumber) {
      return; // Don't allow selection
    }
    
    // Update the form data based on selection
    if (numberTypeSelection === 'shared') {
      setFormData(prev => ({ 
        ...prev, 
        shared: true, 
        exten: '',
      }));
    } else if (numberTypeSelection === 'direct' && selectedExtension) {
      setFormData(prev => ({ 
        ...prev, 
        shared: false, 
        exten: selectedExtension,
        pbxapp: 21,  // Direct application
        pbxdigits: selectedExtension,  // Set the extension in pbxdigits
        pbxdesc: '',  // Direct doesn't need description
      }));
    } else if (numberTypeSelection === 'application') {
      setFormData(prev => ({ ...prev, shared: false, exten: '' }));
    }
    
    setNumberTypeDialog(false);
  };

  const getAppDisplay = (number: PhoneNumber) => {
    let incomingDestination = '';
    let outgoingConfig = '';

    // First determine where INCOMING calls go
    const appName = number.app_name || 'Unknown';
    switch (number.pbxapp) {
      case 1: // Extension Group
        incomingDestination = `Incoming: ${appName} ${number.pbxdigits || ''}`;
        break;
      case 4: // Single Extension
        incomingDestination = `Incoming: ${appName} ${number.pbxdigits || ''}`;
        break;
      case 7: // Voicemail
      case 17: // Voicemail
        incomingDestination = `Incoming: ${appName} ${number.pbxdesc || ''} - ${number.pbxdigits || ''}`;
        break;
      case 16: // IVR
      case 20: // Consecutive Dial
        incomingDestination = `Incoming: ${appName} ${number.pbxdesc || ''}`;
        break;
      case 21: // Direct Number
        incomingDestination = `Incoming: Extension ${number.exten || number.pbxdigits || ''}`;
        break;
      default:
        if (number.pbxdigits && number.pbxdigits !== '0' && number.pbxdigits !== '9999') {
          incomingDestination = `Incoming: ${appName} ${number.pbxdesc || ''} - ${number.pbxdigits || ''}`;
        } else {
          incomingDestination = `Incoming: ${appName}`;
        }
    }

    // Now determine OUTGOING configuration
    if (number.shared == 1) {
      // This is a shared number for outgoing calls
      const sharedInfo = number.shared_extensions || [];
      const isAvailableForAll = number.available_for_all;

      if (isAvailableForAll) {
        outgoingConfig = 'Outgoing: Shared -> Available for all Extensions';
      } else if (sharedInfo.length > 0) {
        if (sharedInfo.length < 10) {
          outgoingConfig = `Outgoing: Shared -> ${sharedInfo.join(', ')}`;
        } else {
          outgoingConfig = `Outgoing: Shared -> ${sharedInfo.length} extensions`;
        }
      } else {
        outgoingConfig = 'Outgoing: Shared';
      }
    } else if (number.pbxapp === 21 || (number.exten && number.exten !== '9999' && !number.shared)) {
      // Direct number - only the specified extension can use it for outgoing
      outgoingConfig = `Outgoing: Direct -> ${number.exten || number.pbxdigits || ''}`;
    } else if (number.exten === '9999' || (!number.exten && !number.shared)) {
      // Application only - no outgoing calls allowed
      outgoingConfig = 'Outgoing: No Outgoing';
    } else {
      // Fallback for any other case
      outgoingConfig = 'Outgoing: Unknown';
    }

    return {
      appName: incomingDestination,  // Show incoming first
      details: outgoingConfig         // Show outgoing second
    };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'shared' ? checked : value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<number>) => {
    const appId = e.target.value as number;
    let autoSelectedDigits = '';
    let autoSelectedDesc = '';

    // Auto-select first item based on application type
    if (appId === 1 && extensionGroups.length > 0) {
      // Extension Group
      autoSelectedDigits = extensionGroups[0].GroupNumber;
      autoSelectedDesc = extensionGroups[0].GroupName;
    } else if (appId === 4 && extensions.length > 0) {
      // Single Extension
      autoSelectedDigits = extensions[0].MyExten;
      autoSelectedDesc = '';
    } else if ((appId === 7 || appId === 17) && voicemails.length > 0) {
      // Voicemail
      autoSelectedDigits = voicemails[0].mailbox;
      autoSelectedDesc = voicemails[0].fullname || '';
    } else if (appId === 16 && ivrs.length > 0) {
      // IVR
      autoSelectedDigits = ivrs[0].Description;
      autoSelectedDesc = ivrs[0].Description;
    } else if (appId === 3 && queues.length > 0) {
      // Call Queue
      autoSelectedDigits = queues[0].extension;
      autoSelectedDesc = queues[0].name;
    } else if (appId === 20 && consecutiveDials.length > 0) {
      // Consecutive Dial
      autoSelectedDigits = consecutiveDials[0].ConsecID;
      autoSelectedDesc = consecutiveDials[0].ConsecID === '16' ? 'IATS-Incoming' : consecutiveDials[0].ConsecName;
    }

    setFormData(prev => ({
      ...prev,
      pbxapp: appId,
      pbxdigits: autoSelectedDigits,
      pbxdesc: autoSelectedDesc,
      recordid: 0    // Reset to 0 to ensure integer value
    }));
  };

  const handleChargePreviewConfirm = async () => {
    logger.debug('Debug message');
    logger.debug('Have login token but no session data - waiting...', selectedLocalNumber);
    logger.debug('Have login token but no session data - waiting...', localNumberType);
    logger.debug('Have login token but no session data - waiting...', localExtension);
    
    try {
      setChargePreviewLoading(true);
      setChargePreviewError(null);
      
      // Order the number
      setOrderingNumber(true);
      const response = await api.get('/endpoints/phone_numbers', {
        params: {
          action: 'order_local_number',
          number: selectedLocalNumber,
          type: localNumberType,
          extension: localNumberType === 'direct' ? localExtension : '',
          available_for_all: localNumberType === 'shared' ? localAvailableForAll : false,
          shared_extensions: localNumberType === 'shared' && !localAvailableForAll ? localSharedExtensions : [],
          with_billing: 'true',
          e911: usePilotAddress ? { use_pilot: true } : {
            firstname: formData.e911_firstname || '',
            lastname: formData.e911_lastname || '',
            street_number: formData.e911_street_number,
            street_name: formData.e911_street_name,
            address_type: formData.e911_address_type,
            unit: formData.e911_unit,
            city: formData.e911_city,
            state: formData.e911_state,
            postal_code: formData.e911_zip,
            other: formData.e911_other || ''
          }
        }
      });
      
      logger.info('Order response received', {
        data: response.data,
        success: response.data?.success,
        status: response.status
      });

      if (response.data.success) {
        let successMsg = 'Phone number ordered successfully';
        
        if (chargeDetails?.includedInPackage) {
          successMsg += ' (Included in your package - no charge)';
        } else if (response.data.billed) {
          successMsg += ` - Charged ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(response.data.amount_charged || chargeDetails?.totalCharge || 0)}`;
        }
        
        setSnackbar({ open: true, message: successMsg, severity: 'success' });
        setChargePreviewOpen(false);
        setOpenDialog(false);
        setSelectedLocalNumber('');
        fetchData();
      } else {
        // Handle case where response doesn't have success: true
        logger.error('Error occurred', response.data);
        setChargePreviewError(response.data.message || 'Failed to order phone number');
      }
    } catch (err: any) {
      logger.error('Error occurred', err);
      logger.error('Error occurred', err.response?.data);
      setChargePreviewError(err.response?.data?.message || 'Failed to order phone number');
    } finally {
      setChargePreviewLoading(false);
      setOrderingNumber(false);
    }
  };

  const handleChargePreviewClose = () => {
    setChargePreviewOpen(false);
    setChargeDetails(null);
    setChargePreviewError(null);
  };

  const handleAddFunds = () => {
    setChargePreviewOpen(false);
    navigate('/billing');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading phone numbers...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Phone Numbers
        </Typography>
        {(member?.memberType === 'admin' || member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Number
          </Button>
        )}
      </Box>

      {isMobile ? (
        // Mobile view - Cards
        <Grid container spacing={2}>
          {phoneNumbers.map((number) => {
            const { appName, details } = getAppDisplay(number);
            return (
              <Grid item xs={12} key={number.did}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Box flex={1}>
                        <Typography variant="h6" gutterBottom>
                          {number.did}
                          {number.has_e911 ? (
                            <Tooltip title={`E911: ${number.e911_address || 'Address configured'}`}>
                              <InfoIcon
                                fontSize="small"
                                sx={{
                                  ml: 1,
                                  color: 'success.main'
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="No 911 setup for this number">
                              <WarningIcon
                                fontSize="small"
                                sx={{
                                  ml: 1,
                                  color: 'error.main'
                                }}
                              />
                            </Tooltip>
                          )}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          {number.CallerIDname}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>App:</strong> {appName}
                          {details && <><br />{details}</>}
                        </Typography>
                        {number.forward_to && (
                          <Typography variant="body2">
                            <strong>Forwarded to:</strong> {number.forward_to}
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" gap={1}>
                        {number.type === 1 && (
                          <Tooltip title="Time Conditions">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/time-conditions?phone_number=${number.did}`)}
                              color="primary"
                            >
                              <ScheduleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(number)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedNumber(number);
                            setDeleteDialog(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        // Desktop view - Table
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Number</TableCell>
                <TableCell>Caller ID Name</TableCell>
                {(member?.memberType === 'reseller_admin' || member?.memberType === 'master_admin') && (
                  <TableCell>Call Limit</TableCell>
                )}
                <TableCell>Send Call to App</TableCell>
                <TableCell>Forwarded To</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {phoneNumbers.map((number) => {
                const { appName, details } = getAppDisplay(number);
                return (
                  <TableRow key={number.did}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {number.did}
                        {number.has_e911 ? (
                          <Tooltip title={`E911: ${number.e911_address || 'Address configured'}`}>
                            <InfoIcon
                              fontSize="small"
                              sx={{
                                ml: 1,
                                color: 'success.main'
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="No 911 setup for this number">
                            <WarningIcon
                              fontSize="small"
                              sx={{
                                ml: 1,
                                color: 'error.main'
                              }}
                            />
                          </Tooltip>
                        )}
                        {number.cancellation_date && (
                          <Tooltip title={`Scheduled for cancellation on ${new Date(number.cancellation_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}>
                            <Box
                              sx={{
                                ml: 1,
                                px: 1,
                                py: 0.5,
                                bgcolor: 'warning.main',
                                color: 'warning.contrastText',
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                              }}
                            >
                              PENDING CANCELLATION
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{number.CallerIDname}</TableCell>
                    {(member?.memberType === 'reseller_admin' || member?.memberType === 'master_admin') && (
                      <TableCell>{number.call_limit}</TableCell>
                    )}
                    <TableCell>
                      {appName}
                      {details && (
                        <Typography variant="body2" color="textSecondary">
                          {details}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{number.forward_to || '-'}</TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        {number.type === 1 && (
                          <Tooltip title="Time Conditions">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/time-conditions?phone_number=${number.did}`)}
                              color="primary"
                            >
                              <ScheduleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(number)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedNumber(number);
                            setDeleteDialog(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {phoneNumbers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No phone numbers found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>{editMode ? 'Edit Phone Number' : 'Add Phone Number'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Tab Navigation - only show tabs in add mode */}
            {!editMode && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 0 }}>
                  {canAddManual && (
                    <Button
                      sx={{
                        borderBottom: selectedTab === 'manual' ? 2 : 0,
                        borderColor: 'primary.main',
                        borderRadius: 0,
                        color: selectedTab === 'manual' ? 'primary.main' : 'text.secondary',
                        fontWeight: selectedTab === 'manual' ? 'bold' : 'normal',
                      }}
                      onClick={() => setSelectedTab('manual')}
                    >
                      Manually Add
                    </Button>
                  )}
                  <Button
                    sx={{
                      borderBottom: selectedTab === 'local' ? 2 : 0,
                      borderColor: 'primary.main',
                      borderRadius: 0,
                      color: selectedTab === 'local' ? 'primary.main' : 'text.secondary',
                      fontWeight: selectedTab === 'local' ? 'bold' : 'normal',
                    }}
                    onClick={() => {
                      setSelectedTab('local');
                      fetchCities();
                    }}
                  >
                    Local Numbers
                  </Button>
                  <Button
                    sx={{
                      borderBottom: selectedTab === 'tollfree' ? 2 : 0,
                      borderColor: 'primary.main',
                      borderRadius: 0,
                      color: selectedTab === 'tollfree' ? 'primary.main' : 'text.secondary',
                      fontWeight: selectedTab === 'tollfree' ? 'bold' : 'normal',
                    }}
                    onClick={() => setSelectedTab('tollfree')}
                  >
                    Toll-Free Numbers
                  </Button>
                  <Button
                    sx={{
                      borderBottom: selectedTab === 'international' ? 2 : 0,
                      borderColor: 'primary.main',
                      borderRadius: 0,
                      color: selectedTab === 'international' ? 'primary.main' : 'text.secondary',
                      fontWeight: selectedTab === 'international' ? 'bold' : 'normal',
                    }}
                    onClick={() => setSelectedTab('international')}
                  >
                    International Numbers
                  </Button>
                </Box>
              </Box>
            )}
            
            {/* Tab Content */}
            {(!editMode && selectedTab === 'manual' && canAddManual) || editMode ? (
              <>
                {/* Manual Tab Content (current form) */}
                
                {/* Cancellation Notice for Edit Mode */}
                {editMode && selectedNumber?.cancellation_date && (
                  <Alert 
                    severity="warning" 
                    sx={{ mb: 2 }}
                    action={
                      <Button 
                        color="inherit" 
                        size="small"
                        onClick={handleReverseCancellation}
                      >
                        Reverse Cancellation
                      </Button>
                    }
                  >
                    <Typography variant="body2">
                      <strong>This phone number is scheduled for cancellation on {new Date(selectedNumber.cancellation_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.</strong>
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      The number will remain active until 11:59 PM on the cancellation date. Click "Reverse Cancellation" to keep this number active.
                    </Typography>
                  </Alert>
                )}
                
                {/* Basic Info Row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Number"
                  name="did"
                  value={formData.did}
                  onChange={handleInputChange}
                  disabled={editMode}
                  placeholder="10 digits"
                  InputProps={{
                    readOnly: editMode,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Caller ID Name"
                  name="CallerIDname"
                  value={formData.CallerIDname}
                  onChange={handleInputChange}
                  disabled={editMode}
                  helperText={editMode ? "Caller ID Name is read-only" : ""}
                  InputProps={{
                    readOnly: editMode,
                  }}
                />
              </Grid>
              {(member?.memberType === 'reseller_admin' || member?.memberType === 'master_admin') && (
                <Grid item xs={12} md={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Call Limit"
                      name="call_limit"
                      type="number"
                      value={formData.call_limit}
                      onChange={handleInputChange}
                      InputProps={{
                        readOnly: !editCallLimit,
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setEditCallLimit(!editCallLimit)}
                      color={editCallLimit ? "success" : "primary"}
                    >
                      {editCallLimit ? <CheckIcon /> : <EditIcon />}
                    </IconButton>
                  </Box>
                </Grid>
              )}
            </Grid>
            
            {/* Recording and Application Row - Hide application dropdowns for Direct numbers */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel>Recording played before Application</InputLabel>
                  <Select
                    value={formData.recordid}
                    onChange={(e) => setFormData(prev => ({ ...prev, recordid: e.target.value as number }))}
                    label="Recording played before Application"
                    size="small"
                  >
                    <MenuItem value={0}>No Recording</MenuItem>
                    {recordings.map((rec) => (
                      <MenuItem key={rec.RecordID} value={rec.RecordID}>
                        {rec.Name} - {rec.Notes}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Only show application dropdowns if NOT a Direct number */}
              {(!formData.shared && formData.exten) ? (
                // Direct number - show a message instead of dropdowns
                <Grid item xs={12} md={8}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#f5f5f5', 
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    height: '40px'
                  }}>
                    <Typography variant="body2" color="textSecondary">
                      Direct Number → Extension {formData.exten} (Change number type to modify)
                    </Typography>
                  </Box>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" variant="outlined">
                      <InputLabel>Send the caller to this Application</InputLabel>
                      <Select
                        value={formData.pbxapp}
                        onChange={handleSelectChange}
                        label="Send the caller to this Application"
                        size="small"
                      >
                        {applications.map((app) => (
                          <MenuItem key={app.app_id} value={app.app_id}>
                            {app.app_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
              
              {/* Dynamic dropdown based on selected application */}
              {(formData.pbxapp == 1) && extensionGroups.length > 0 && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="extension-group-label">Extension Group</InputLabel>
                    <Select
                      labelId="extension-group-label"
                      value={(formData.pbxdigits && extensionGroups.find(g => g.GroupNumber === formData.pbxdigits)) ? formData.pbxdigits : extensionGroups[0].GroupNumber}
                      onChange={(e) => {
                        const groupNumber = e.target.value as string;
                        const group = extensionGroups.find(g => g.GroupNumber === groupNumber);
                        setFormData(prev => ({
                          ...prev,
                          pbxdigits: groupNumber,
                          pbxdesc: group ? group.GroupName : ''
                        }));
                      }}
                      label="Extension Group"
                      size="small"
                      renderValue={(value) => {
                        const group = extensionGroups.find(g => g.GroupNumber === value);
                        return group ? `${group.GroupName} (${group.GroupNumber})` : (extensionGroups[0] ? `${extensionGroups[0].GroupName} (${extensionGroups[0].GroupNumber})` : '');
                      }}
                    >
                      {extensionGroups.map((group) => (
                        <MenuItem key={group.GroupNumber} value={group.GroupNumber}>
                          {group.GroupName} ({group.GroupNumber})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              {(formData.pbxapp == 4) && extensions.length > 0 && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="single-extension-label">Extension</InputLabel>
                    <Select
                      labelId="single-extension-label"
                      value={formData.pbxdigits || extensions[0].MyExten}
                      onChange={(e) => setFormData(prev => ({ ...prev, pbxdigits: e.target.value as string }))}
                      label="Extension"
                      size="small"
                      renderValue={(value) => {
                        const ext = extensions.find(e => e.MyExten === value);
                        return ext ? `${ext.MyExten}${ext.fullname ? ` - ${ext.fullname}` : ''}` : (extensions[0] ? `${extensions[0].MyExten}${extensions[0].fullname ? ` - ${extensions[0].fullname}` : ''}` : '');
                      }}
                    >
                      {extensions.map((ext) => (
                        <MenuItem key={ext.MyExten} value={ext.MyExten}>
                          {ext.MyExten} {ext.fullname && `- ${ext.fullname}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              {(formData.pbxapp == 7 || formData.pbxapp == 17) && voicemails.length > 0 && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="voicemail-label">Voicemail</InputLabel>
                    <Select
                      labelId="voicemail-label"
                      value={formData.pbxdigits || voicemails[0].mailbox}
                      onChange={(e) => {
                        const mailbox = e.target.value as string;
                        const vm = voicemails.find(v => v.mailbox === mailbox);
                        setFormData(prev => ({
                          ...prev,
                          pbxdigits: mailbox,
                          pbxdesc: vm ? (vm.fullname || '') : ''
                        }));
                      }}
                      label="Voicemail"
                      size="small"
                      renderValue={(value) => {
                        const vm = voicemails.find(v => v.mailbox === value);
                        return vm ? (vm.fullname ? `${vm.fullname} (${vm.mailbox})` : `Voicemail ${vm.mailbox}`) : (voicemails[0] ? (voicemails[0].fullname ? `${voicemails[0].fullname} (${voicemails[0].mailbox})` : `Voicemail ${voicemails[0].mailbox}`) : '');
                      }}
                    >
                      {voicemails.map((vm) => (
                        <MenuItem key={vm.mailbox} value={vm.mailbox}>
                          {vm.fullname ? `${vm.fullname} (${vm.mailbox})` : `Voicemail ${vm.mailbox}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              {(formData.pbxapp == 16) && ivrs.length > 0 && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="ivr-label">IVR</InputLabel>
                    <Select
                      labelId="ivr-label"
                      value={formData.pbxdigits || ivrs[0].Description}
                      onChange={(e) => {
                        const selectedDescription = e.target.value as string;
                        setFormData(prev => ({
                          ...prev,
                          pbxdigits: selectedDescription,  // Store Description in pbxdigits to match old system
                          pbxdesc: selectedDescription
                        }));
                      }}
                      label="IVR"
                      size="small"
                      renderValue={(value) => {
                        return value ? `IVR: ${value}` : (ivrs[0] ? `IVR: ${ivrs[0].Description}` : '');
                      }}
                    >
                      {ivrs.map((ivr) => (
                        <MenuItem key={ivr.ivrID} value={ivr.Description}>
                          IVR: {ivr.Description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              {(formData.pbxapp == 3) && queues.length > 0 && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="call-queue-label">Call Queue</InputLabel>
                    <Select
                      labelId="call-queue-label"
                      value={queues.some(q => q.extension === formData.pbxdigits) ? formData.pbxdigits : (queues[0]?.extension || '')}
                      onChange={(e) => {
                        const extension = e.target.value as string;
                        const queue = queues.find(q => q.extension === extension);
                        setFormData(prev => ({
                          ...prev,
                          pbxdigits: extension,
                          pbxdesc: queue ? queue.name : ''
                        }));
                      }}
                      label="Call Queue"
                      size="small"
                    >
                      {queues.map((queue) => (
                        <MenuItem key={queue.extension} value={queue.extension}>
                          {queue.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {(formData.pbxapp == 20) && consecutiveDials.length > 0 && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="consecutive-dial-label">Consecutive Dial</InputLabel>
                    <Select
                      labelId="consecutive-dial-label"
                      value={formData.pbxdigits || consecutiveDials[0].ConsecID}
                      onChange={(e) => {
                        const consecID = e.target.value as string;
                        let consecName = '';
                        if (consecID === '16') {
                          consecName = 'IATS-Incoming';
                        } else {
                          const dial = consecutiveDials.find(d => d.ConsecID === consecID);
                          consecName = dial ? dial.ConsecName : '';
                        }
                        setFormData(prev => ({
                          ...prev,
                          pbxdigits: consecID,
                          pbxdesc: consecName
                        }));
                      }}
                      label="Consecutive Dial"
                      size="small"
                      renderValue={(value) => {
                        if (value === '16') return 'IATS-Incoming';
                        const dial = consecutiveDials.find(d => d.ConsecID === value);
                        return dial ? dial.ConsecName : (consecutiveDials[0] ? (consecutiveDials[0].ConsecID === '16' ? 'IATS-Incoming' : consecutiveDials[0].ConsecName) : '');
                      }}
                    >
                      {consecutiveDials.map((dial) => (
                        <MenuItem key={dial.ConsecID} value={dial.ConsecID}>
                          {dial.ConsecID === '16' ? 'IATS-Incoming' : dial.ConsecName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
                </>
              )}
            </Grid>
            
            {/* Number Type Display/Selection */}
            {editMode ? (
              // Edit mode - show current type with edit button
              <Box sx={{ 
                bgcolor: '#f0f8ff', 
                p: 2, 
                border: '2px solid', 
                borderColor: 'primary.main',
                borderRadius: 1,
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="subtitle1">
                  <strong>Number Type:</strong> {getNumberTypeDisplay()}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleNumberTypeOpen}
                  color="primary"
                >
                  <EditIcon />
                </IconButton>
              </Box>
            ) : (
              // Add mode - show RadioGroup for number type selection
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Number Type:</strong>
                </Typography>
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={formData.shared ? 'shared' : (formData.exten ? 'direct' : 'application')}
                    onChange={(e) => {
                      logger.debug(`Radio change from ${formData.shared ? 'shared' : (formData.exten ? 'direct' : 'application')} to ${e.target.value}`);
                      const value = e.target.value;
                      if (value === 'application') {
                        setFormData(prev => ({ ...prev, shared: false, exten: '' }));
                        setSharedExtensions([]);
                        setAvailableForAll(false);
                      } else if (value === 'direct') {
                        setFormData(prev => ({ ...prev, shared: false, exten: extensions[0]?.MyExten || '' }));
                        setSharedExtensions([]);
                        setAvailableForAll(false);
                        fetchAvailableExtensions();
                      } else if (value === 'shared') {
                        logger.debug('Debug message');
                        setFormData(prev => ({ ...prev, shared: true, exten: '' }));
                        // Don't reset sharedExtensions if they're already set - preserve the selection
                        if (sharedExtensions.length === 0 && formData.did) {
                          // Only fetch from database if we don't have any selections yet
                          fetchSharedExtensions(formData.did);
                        }
                        // Keep availableForAll as is unless we need to reset it
                      }
                    }}
                  >
                    <FormControlLabel
                      value="application"
                      control={<Radio />}
                      label="Application only"
                    />
                    <FormControlLabel
                      value="direct"
                      control={<Radio />}
                      label="Direct Number"
                    />
                    <Box display="flex" alignItems="center" gap={1}>
                      <FormControlLabel
                        value="shared"
                        control={<Radio />}
                        label="Shared for Outgoing Calls"
                      />
                      {formData.shared && (
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={availableForAll}
                              onChange={(e) => setAvailableForAll(e.target.checked)}
                              size="small"
                            />
                          }
                          label="Available for all extensions"
                        />
                      )}
                    </Box>
                  </RadioGroup>
                  
                  {/* Extension selection for Direct Number */}
                  {(formData.shared === false && (formData.exten !== '' || formData.pbxapp === 0)) && (
                    <Box sx={{ mt: 1, ml: 3 }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Extension</InputLabel>
                        <Select
                          value={formData.exten}
                          onChange={(e) => setFormData(prev => ({ ...prev, exten: e.target.value }))}
                          label="Extension"
                        >
                          {extensions.map((ext) => (
                            <MenuItem key={ext.MyExten} value={ext.MyExten}>
                              {ext.MyExten} {ext.fullname && `- ${ext.fullname}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  
                  {/* Shared extensions selection */}
                  {formData.shared && !availableForAll && (
                    <Box sx={{ mt: 1, ml: 3 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Select extensions that can use this number for outgoing calls:
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: extensions.length > 25 ? 'column' : 'row',
                        flexWrap: extensions.length <= 25 ? 'wrap' : 'nowrap',
                        gap: 1,
                        maxHeight: extensions.length > 25 ? '300px' : 'auto',
                        overflowY: extensions.length > 25 ? 'auto' : 'visible',
                        border: extensions.length > 25 ? '1px solid #e0e0e0' : 'none',
                        borderRadius: extensions.length > 25 ? 1 : 0,
                        p: extensions.length > 25 ? 1 : 0
                      }}>
                        {extensions.map((ext) => (
                          <FormControlLabel
                            key={ext.MyExten}
                            sx={{ 
                              width: extensions.length > 25 ? '100%' : 'auto',
                              m: 0
                            }}
                            control={
                              <Checkbox
                                checked={sharedExtensions.includes(ext.MyExten)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSharedExtensions(prev => [...prev, ext.MyExten]);
                                  } else {
                                    setSharedExtensions(prev => prev.filter(e => e !== ext.MyExten));
                                  }
                                }}
                                size="small"
                              />
                            }
                            label={`${ext.MyExten}${ext.fullname ? ` - ${ext.fullname}` : ''}`}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </FormControl>
              </Box>
            )}
            
            {/* E911 Information Section */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                E911 Information
              </Typography>
              
              {/* Show pilot number info if available */}
              {pilotNumber && (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Main Number:</strong> {pilotNumber.CallerIDnumber} - {pilotNumber.CallerIDname}
                  </Typography>
                  {pilotE911Info && (
                    <Typography variant="body2" color="textSecondary">
                      <strong>E911 Address:</strong> {[
                        pilotE911Info.street_number,
                        pilotE911Info.street_name,
                        pilotE911Info.city,
                        pilotE911Info.state,
                        pilotE911Info.postal_code
                      ].filter(Boolean).join(' ')}
                    </Typography>
                  )}
                </Box>
              )}
              
              {/* Address Selection */}
              <Box sx={{ mb: 2 }}>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={e911Option}
                    onChange={(e) => {
                      const option = e.target.value as 'pilot' | 'new' | 'keep';
                      setE911Option(option);
                      setUsePilotAddress(option === 'pilot');

                      if (option === 'pilot' || option === 'keep') {
                        // Clear custom address fields when not entering new
                        setFormData(prev => ({
                          ...prev,
                          e911_address: '',
                          e911_unit: '',
                        }));
                      }
                    }}
                  >
                    {/* Don't show "Use main number's address" option if editing the pilot number itself */}
                    {pilotE911Info && !(editMode && selectedNumber && pilotNumber && selectedNumber.did === pilotNumber.CallerIDnumber) && (
                      <FormControlLabel
                        value="pilot"
                        control={<Radio />}
                        label="Use main number's address"
                      />
                    )}
                    {editMode && currentE911Address && (
                      <FormControlLabel
                        value="keep"
                        control={<Radio />}
                        label="Keep current address"
                      />
                    )}
                    <FormControlLabel
                      value="new"
                      control={<Radio />}
                      label="Enter a new address"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
              
              {/* Show current E911 address when "Keep current address" is selected */}
              {e911Option === 'keep' && currentE911Address && (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Current E911 Address:</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {currentE911Address}
                  </Typography>
                </Box>
              )}
              
              {/* E911 Address Fields - Only show when "Enter a new address" is selected */}
              {e911Option === 'new' && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    {/* Unit Number and Address on same row */}
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Unit Number"
                        placeholder="Apt, Suite, Floor, etc."
                        value={formData.e911_unit || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, e911_unit: e.target.value }))}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={9}>
                      <GooglePlacesAutocomplete
                        value={''}
                        onChange={(value) => setFormData(prev => ({ ...prev, e911_address: value }))}
                        onPlaceSelected={(place) => {
                          // Parse the address components
                          const components = place.address_components || [];
                          let streetNumber = '';
                          let streetName = '';
                          let city = '';
                          let state = '';
                          let zipCode = '';
                          
                          // Log the raw Google Places data
                          const addressComponents: any[] = [];
                          
                          components.forEach((component: google.maps.GeocoderAddressComponent) => {
                            const types = component.types;
                            logger.debug('Address component:', { types: component.types, name: component.long_name });
                            
                            // Collect all components for logging
                            addressComponents.push({
                              types: component.types,
                              long_name: component.long_name,
                              short_name: component.short_name
                            });
                            
                            if (types.includes('street_number')) {
                              streetNumber = component.long_name;
                            }
                            if (types.includes('route')) {
                              streetName = component.long_name;
                            }
                            if (types.includes('locality')) {
                              city = component.long_name;
                            }
                            if (types.includes('administrative_area_level_1')) {
                              state = component.short_name;
                            }
                            if (types.includes('postal_code')) {
                              zipCode = component.long_name;
                            }
                          });
                          
                          // Send to logging endpoint
                          api.post('/endpoints/log_address', {
                            phone_number: selectedNumber?.did,
                            formatted_address: place.formatted_address,
                            address_components: addressComponents,
                            parsed_values: {
                              street_number: streetNumber,
                              street_name: streetName,
                              city: city,
                              state: state,
                              zip: zipCode
                            },
                            timestamp: new Date().toISOString()
                          }).catch(err => {
                            logger.error('Error occurred', err);
                          });
                          
                          // Update all E911 fields
                          setFormData(prev => ({
                            ...prev,
                            e911_address: place.formatted_address || '',
                            e911_street_number: streetNumber,
                            e911_street_name: streetName,
                            e911_city: city,
                            e911_state: state,
                            e911_zip: zipCode,
                          }));
                        }}
                        label="Address"
                        placeholder=""
                      />
                    </Grid>

                    {/* Show extracted address components */}
                    {formData.e911_address && (
                      <>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Street Number"
                            value={formData.e911_street_number || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, e911_street_number: e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={9}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Street Name"
                            value={formData.e911_street_name || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, e911_street_name: e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="City"
                            value={formData.e911_city || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, e911_city: e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Province/State"
                            value={formData.e911_state || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, e911_state: e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Postal Code"
                            value={formData.e911_zip || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, e911_zip: e.target.value }))}
                            helperText={!formData.e911_zip ? "⚠️ Required for E911" : ""}
                            error={!formData.e911_zip}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Special Instructions Field */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Special Instructions"
                        placeholder=""
                        value={formData.e911_other || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, e911_other: e.target.value }))}
                        multiline
                        rows={2}
                        helperText="Optional: Add any special instructions for emergency responders (e.g., Gate code, building entrance details, etc.)"
                      />
                    </Grid>
                    
                    {/* Confirm Address Button */}
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => {
                          if (formData.e911_address) {
                            setE911AddressConfirmed(true);
                            setSnackbar({ open: true, message: 'E911 address confirmed', severity: 'success' });
                          } else {
                            setSnackbar({ open: true, message: 'Please select an address', severity: 'error' });
                          }
                        }}
                      >
                        Confirm Address
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
              </Box>
              </>
            ) : !editMode && selectedTab === 'local' ? (
              <>
                {/* Local Numbers Tab Content */}
                <Typography variant="h6" gutterBottom>
                  Local Numbers
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small" variant="outlined">
                      <InputLabel>City</InputLabel>
                      <Select
                        value={localCity}
                        onChange={async (e) => {
                          setLocalCity(e.target.value);
                          setLocalArea('');
                          setAvailableNumbers([]);
                          setSelectedLocalNumber('');
                          if (e.target.value) {
                            await fetchAreas(e.target.value);
                          }
                        }}
                        label="City"
                      >
                        <MenuItem value="">Select city</MenuItem>
                        {cities.map((city: string) => (
                          <MenuItem key={city} value={city}>
                            {city}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small" variant="outlined" disabled={!localCity}>
                      <InputLabel>Area</InputLabel>
                      <Select
                        value={localArea}
                        onChange={(e) => {
                          setLocalArea(e.target.value);
                          const selectedOption = e.target.value;
                          if (selectedOption) {
                            // Find the selected area object
                            const selectedAreaObj = areas.find((area: any) =>
                              `${area.name} (${area.npa}-${area.nxx})` === selectedOption
                            );
                            if (selectedAreaObj) {
                              // Add small delay to avoid race conditions with auth
                              setTimeout(() => {
                                searchAvailableNumbers(selectedAreaObj.npa, selectedAreaObj.nxx);
                              }, 100);
                            }
                          }
                        }}
                        label="Area"
                      >
                        <MenuItem value="">Select area</MenuItem>
                        {areas.map((area: any) => (
                          <MenuItem 
                            key={`${area.npa}-${area.nxx}`} 
                            value={`${area.name} (${area.npa}-${area.nxx})`}
                          >
                            {area.name} ({area.npa}-{area.nxx})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    {searchingNumbers ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                        <Typography sx={{ ml: 2 }}>Searching available numbers...</Typography>
                      </Box>
                    ) : (
                      <FormControl fullWidth size="small" variant="outlined" disabled={!availableNumbers.length}>
                        <InputLabel>Number</InputLabel>
                        <Select
                          value={selectedLocalNumber}
                          onChange={(e) => setSelectedLocalNumber(e.target.value)}
                          label="Number"
                        >
                          <MenuItem value="">Select number</MenuItem>
                          {availableNumbers.map((item: any) => {
                            const num = typeof item === 'string' ? item : item.number;
                            return (
                              <MenuItem key={num} value={num}>
                                {num.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                  
                  {/* Number Type Options */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      <strong>Number Type:</strong>
                    </Typography>
                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup
                        value={localNumberType}
                        onChange={(e) => {
                          const value = e.target.value as 'application' | 'direct' | 'shared';
                          setLocalNumberType(value);
                          if (value === 'application') {
                            setLocalExtension('');
                            setLocalSharedExtensions([]);
                            setLocalAvailableForAll(false);
                          } else if (value === 'direct') {
                            setLocalExtension(extensions[0]?.MyExten || '');
                            setLocalSharedExtensions([]);
                            setLocalAvailableForAll(false);
                          } else if (value === 'shared') {
                            setLocalExtension('');
                            setLocalSharedExtensions([]);
                            setLocalAvailableForAll(false);
                          }
                        }}
                      >
                        <FormControlLabel
                          value="application"
                          control={<Radio />}
                          label="Application only"
                          disabled={!selectedLocalNumber}
                        />
                        <FormControlLabel
                          value="direct"
                          control={<Radio />}
                          label="Direct Number"
                          disabled={!selectedLocalNumber}
                        />
                        <Box display="flex" alignItems="center" gap={1}>
                          <FormControlLabel
                            value="shared"
                            control={<Radio />}
                            label="Shared for Outgoing Calls"
                            disabled={!selectedLocalNumber}
                          />
                          {localNumberType === 'shared' && (
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={localAvailableForAll}
                                  onChange={(e) => setLocalAvailableForAll(e.target.checked)}
                                  size="small"
                                />
                              }
                              label="Available for all extensions"
                            />
                          )}
                        </Box>
                      </RadioGroup>
                      
                      {/* Extension selection for Direct Number */}
                      {localNumberType === 'direct' && (
                        <Box sx={{ mt: 1, ml: 3 }}>
                          <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Extension</InputLabel>
                            <Select
                              value={localExtension}
                              onChange={(e) => setLocalExtension(e.target.value)}
                              label="Extension"
                              disabled={!selectedLocalNumber}
                            >
                              {extensions.map((ext) => (
                                <MenuItem key={ext.MyExten} value={ext.MyExten}>
                                  {ext.MyExten} {ext.fullname && `- ${ext.fullname}`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      )}
                      
                      {/* Shared extensions selection */}
                      {localNumberType === 'shared' && !localAvailableForAll && (
                        <Box sx={{ mt: 1, ml: 3 }}>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            Select extensions that can use this number for outgoing calls:
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: extensions.length > 25 ? 'column' : 'row',
                            flexWrap: extensions.length <= 25 ? 'wrap' : 'nowrap',
                            gap: 1,
                            maxHeight: extensions.length > 25 ? '300px' : 'auto',
                            overflowY: extensions.length > 25 ? 'auto' : 'visible',
                            border: extensions.length > 25 ? '1px solid #e0e0e0' : 'none',
                            borderRadius: extensions.length > 25 ? 1 : 0,
                            p: extensions.length > 25 ? 1 : 0
                          }}>
                            {extensions.map((ext) => (
                              <FormControlLabel
                                key={ext.MyExten}
                                sx={{ 
                                  width: extensions.length > 25 ? '100%' : 'auto',
                                  m: 0
                                }}
                                control={
                                  <Checkbox
                                    checked={localSharedExtensions.includes(ext.MyExten)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setLocalSharedExtensions(prev => [...prev, ext.MyExten]);
                                      } else {
                                        setLocalSharedExtensions(prev => prev.filter(e => e !== ext.MyExten));
                                      }
                                    }}
                                    size="small"
                                  />
                                }
                                label={`${ext.MyExten}${ext.fullname ? ` - ${ext.fullname}` : ''}`}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </FormControl>
                  </Grid>
                  
                  {/* E911 Section for Local Numbers */}
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        E911 Information
                      </Typography>
                      
                      {/* Show pilot number info if available */}
                      {pilotNumber && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="body2" gutterBottom>
                            <strong>Main/Pilot Number:</strong> {pilotNumber.CallerIDnumber} - {pilotNumber.CallerIDname}
                          </Typography>
                          {pilotE911Info && (
                            <Typography variant="body2" color="textSecondary">
                              <strong>E911 Address:</strong> {[
                                pilotE911Info.street_number,
                                pilotE911Info.street_name,
                                pilotE911Info.city,
                                pilotE911Info.state,
                                pilotE911Info.postal_code
                              ].filter(Boolean).join(' ')}
                            </Typography>
                          )}
                        </Box>
                      )}
                      
                      {/* Address Selection */}
                      <Box sx={{ mb: 2 }}>
                        <FormControl component="fieldset">
                          <RadioGroup
                            value={usePilotAddress ? 'pilot' : 'new'}
                            onChange={(e) => {
                              const usePilot = e.target.value === 'pilot';
                              setUsePilotAddress(usePilot);
                            }}
                          >
                            {pilotE911Info && (
                              <FormControlLabel
                                value="pilot"
                                control={<Radio />}
                                label="Use main number's address"
                              />
                            )}
                            <FormControlLabel
                              value="new"
                              control={<Radio />}
                              label="Enter a new address"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Box>
                      
                      {/* New Address Fields - only show if not using pilot */}
                      {!usePilotAddress && (
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={9}>
                            <GooglePlacesAutocomplete
                              value={''}
                              onChange={(value) => setFormData(prev => ({ ...prev, e911_address: value }))}
                              onPlaceSelected={(place) => {
                                // Same logic as manual tab
                                const components = place.address_components || [];
                                let streetNumber = '';
                                let streetName = '';
                                let city = '';
                                let state = '';
                                let zipCode = '';
                                
                                // Log the raw Google Places data
                                const addressComponents: any[] = [];
                                
                                components.forEach((component: google.maps.GeocoderAddressComponent) => {
                                  const types = component.types;
                                  logger.debug('Address component:', { types: component.types, name: component.long_name });
                                  
                                  // Collect all components for logging
                                  addressComponents.push({
                                    types: component.types,
                                    long_name: component.long_name,
                                    short_name: component.short_name
                                  });
                                  
                                  if (types.includes('street_number')) {
                                    streetNumber = component.long_name;
                                  }
                                  if (types.includes('route')) {
                                    streetName = component.long_name;
                                  }
                                  if (types.includes('locality')) {
                                    city = component.long_name;
                                  }
                                  if (types.includes('administrative_area_level_1')) {
                                    state = component.short_name;
                                  }
                                  if (types.includes('postal_code')) {
                                    zipCode = component.long_name;
                                  }
                                });
                                
                                // Send to logging endpoint
                                api.post('/endpoints/log_address', {
                                  phone_number: formData.did,
                                  formatted_address: place.formatted_address,
                                  address_components: addressComponents,
                                  parsed_values: {
                                    street_number: streetNumber,
                                    street_name: streetName,
                                    city: city,
                                    state: state,
                                    zip: zipCode
                                  },
                                  timestamp: new Date().toISOString()
                                }).catch(err => {
                                  logger.error('Error occurred', err);
                                });
                                
                                setFormData(prev => ({
                                  ...prev,
                                  e911_address: place.formatted_address || '',
                                  e911_street_number: streetNumber,
                                  e911_street_name: streetName,
                                  e911_city: city,
                                  e911_state: state,
                                  e911_zip: zipCode,
                                }));
                              }}
                              label="E911 Address"
                              placeholder="Start typing an address..."
                              helperText="Search for and select a verified address"
                            />
                          </Grid>

                          {/* Show extracted address components */}
                          {formData.e911_address && (
                            <>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Street Number"
                                  value={formData.e911_street_number || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, e911_street_number: e.target.value }))}
                                />
                              </Grid>
                              <Grid item xs={12} sm={9}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Street Name"
                                  value={formData.e911_street_name || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, e911_street_name: e.target.value }))}
                                />
                              </Grid>
                              <Grid item xs={12} sm={5}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="City"
                                  value={formData.e911_city || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, e911_city: e.target.value }))}
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Province/State"
                                  value={formData.e911_state || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, e911_state: e.target.value }))}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Postal Code"
                                  value={formData.e911_zip || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, e911_zip: e.target.value }))}
                                  helperText={!formData.e911_zip ? "⚠️ Required for E911" : ""}
                                  error={!formData.e911_zip}
                                />
                              </Grid>
                            </>
                          )}

                          <Grid item xs={12} md={3}>
                            <Button
                              variant="contained"
                              color={e911AddressConfirmed ? "success" : "primary"}
                              onClick={() => {
                                if (formData.e911_street_number && formData.e911_street_name && formData.e911_city && formData.e911_state && formData.e911_zip) {
                                  setE911AddressConfirmed(true);
                                  setSnackbar({ open: true, message: 'E911 address confirmed', severity: 'success' });
                                } else {
                                  setSnackbar({ open: true, message: 'Please select a valid address from the suggestions', severity: 'error' });
                                }
                              }}
                              disabled={!formData.e911_street_number || !formData.e911_street_name}
                              fullWidth
                            >
                              {e911AddressConfirmed ? 'Address Confirmed ✓' : 'Confirm Address'}
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </>
            ) : !editMode && selectedTab === 'tollfree' ? (
              <Typography variant="body1" color="textSecondary">
                Toll-Free Numbers - Coming Soon
              </Typography>
            ) : !editMode && selectedTab === 'international' ? (
              <Typography variant="body1" color="textSecondary">
                International Numbers - Coming Soon
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={async () => {
              if (selectedTab === 'local' && !editMode) {
                // Handle local number order
                if (!selectedLocalNumber) {
                  setSnackbar({ open: true, message: 'Please select a number', severity: 'error' });
                  return;
                }
                
                if (localNumberType === 'direct' && !localExtension) {
                  setSnackbar({ open: true, message: 'Please select an extension for direct number', severity: 'error' });
                  return;
                }
                
                if (localNumberType === 'shared' && !localAvailableForAll && localSharedExtensions.length === 0) {
                  setSnackbar({ open: true, message: 'Please select at least one extension for shared number', severity: 'error' });
                  return;
                }
                
                if (!usePilotAddress && (!formData.e911_street_number || !formData.e911_street_name || !formData.e911_city || !formData.e911_state || !formData.e911_zip)) {
                  setSnackbar({ open: true, message: 'Please complete the E911 address information', severity: 'error' });
                  return;
                }
                
                // Get charge preview
                setChargePreviewError(null);
                try {
                  const customerId = customerFilter.customerId || member?.customerID;
                  const params = getCustomerParams();
                  const previewUrl = createUrlWithCustomerParams('/endpoints/charge-preview', params);
                  const previewResponse = await api.post(previewUrl, {
                    service_type: 'phone_number',
                    service_id: selectedLocalNumber,
                    monthly_rate: 6.95, // Will be updated by the endpoint based on rate class
                    customerId: customerId
                  });
                  
                  if (previewResponse.data.success) {
                    // Map snake_case API response to camelCase for component
                    const apiData = previewResponse.data.data;
                    setChargeDetails({
                      serviceType: apiData.service_type,
                      serviceId: apiData.service_id,
                      description: apiData.description,
                      monthlyRate: apiData.monthly_rate,
                      proratedDays: apiData.prorated_days,
                      proratedAmount: apiData.prorated_amount,
                      currentMonthCharge: apiData.current_month_charge,
                      nextMonthCharge: apiData.next_month_charge,
                      subtotal: apiData.subtotal,
                      taxDetails: apiData.tax_details,
                      totalTax: apiData.total_tax,
                      totalCharge: apiData.total_charge,
                      currentBalance: apiData.current_balance,
                      newBalance: apiData.new_balance,
                      sufficientFunds: apiData.sufficient_funds,
                      billingPeriod: apiData.billing_period,
                      includedInPackage: apiData.included_in_package,
                      province: apiData.province,
                      provinceName: apiData.province_name
                    });
                    setChargePreviewOpen(true);
                    return; // Don't proceed with order yet
                  }
                } catch (err: any) {
                  logger.error('Error occurred', err);
                  setSnackbar({ 
                    open: true, 
                    message: err.response?.data?.message || 'Failed to calculate charges', 
                    severity: 'error' 
                  });
                  return;
                }
                
                // Order the number
                setOrderingNumber(true);
                try {
                  const response = await api.get('/endpoints/phone_numbers?action=order_local_number', {
                    params: {
                      number: selectedLocalNumber,
                      type: localNumberType,
                      extension: localNumberType === 'direct' ? localExtension : '',
                      available_for_all: localNumberType === 'shared' ? localAvailableForAll : false,
                      shared_extensions: localNumberType === 'shared' && !localAvailableForAll ? localSharedExtensions : [],
                      e911: usePilotAddress ? { use_pilot: true } : {
                        firstname: formData.e911_firstname || '',
                        lastname: formData.e911_lastname || '',
                        street_number: formData.e911_street_number,
                        street_name: formData.e911_street_name,
                        address_type: formData.e911_address_type,
                        unit: formData.e911_unit,
                        city: formData.e911_city,
                        state: formData.e911_state,
                        postal_code: formData.e911_zip,
                        other: formData.e911_other || ''
                      }
                    }
                  });
                  
                  if (response.data.success) {
                    const message = 'Number ordered successfully';
                    setSnackbar({ open: true, message, severity: 'success' });
                    handleCloseDialog();
                    fetchData();
                  } else {
                    setSnackbar({ open: true, message: response.data.message || 'Failed to order number', severity: 'error' });
                  }
                } catch (err: any) {
                  const errorMessage = err.response?.data?.message || 'Failed to order number';
                  if (err.response?.data?.required && err.response?.data?.available !== undefined) {
                    const required = parseFloat(err.response.data.required).toFixed(2);
                    const available = parseFloat(err.response.data.available).toFixed(2);
                    setSnackbar({ 
                      open: true, 
                      message: `Insufficient credit balance. Required: $${required}, Available: $${available}. Please add funds to your account.`, 
                      severity: 'error' 
                    });
                  } else {
                    setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                  }
                } finally {
                  setOrderingNumber(false);
                }
              } else {
                // Handle manual add/edit
                handleSubmit();
              }
            }} 
            variant="contained" 
            color="primary"
            disabled={orderingNumber}
          >
            {orderingNumber ? <CircularProgress size={24} /> : (editMode ? 'Save' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Cancel Phone Number</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to cancel the phone number {selectedNumber?.did}?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Important:</strong> This phone number will remain active and available for use until the end of your current billing period.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              After the cancellation date, this number will be released and you will no longer be able to use it.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Keep Number</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Cancel Number
          </Button>
        </DialogActions>
      </Dialog>

      {/* Number Type Dialog */}
      <Dialog open={numberTypeDialog} onClose={() => setNumberTypeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Number Type</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={numberTypeSelection}
                onChange={(e) => {
                  const value = e.target.value;
                  setNumberTypeSelection(value);
                  setSelectedExtension('');
                  setIsPilotNumber(false);
                }}
              >
                <FormControlLabel
                  value="application"
                  control={<Radio />}
                  label="Application only"
                />
                <FormControlLabel
                  value="direct"
                  control={<Radio />}
                  label="Direct Number"
                />
                <Box display="flex" alignItems="center" gap={1}>
                  <FormControlLabel
                    value="shared"
                    control={<Radio />}
                    label="Shared for Outgoing Calls"
                  />
                  {numberTypeSelection === 'shared' && (
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={availableForAll}
                          onChange={(e) => setAvailableForAll(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Available for all extensions"
                    />
                  )}
                </Box>
              </RadioGroup>
            </FormControl>
            
            {/* Extension dropdown for Direct numbers */}
            {numberTypeSelection === 'direct' && (
              <Box sx={{ mt: 3 }}>
                <FormControl fullWidth size="small" required error={!selectedExtension}>
                  <InputLabel>Select the Extension for the Direct Number *</InputLabel>
                  <Select
                    value={selectedExtension}
                    onChange={async (e) => {
                      const ext = e.target.value as string;
                      setSelectedExtension(ext);
                      if (ext) {
                        await checkPilotNumber(ext);
                      } else {
                        setIsPilotNumber(false);
                      }
                    }}
                    label="Select the Extension for the Direct Number *"
                  >
                    {availableExtensions.map((ext) => (
                      <MenuItem key={ext.MyExten} value={ext.MyExten}>
                        {ext.MyExten} {ext.fullname && `- ${ext.fullname}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {isPilotNumber && selectedExtension && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    This extension is a pilot number and cannot be used for direct number assignment.
                  </Alert>
                )}
              </Box>
            )}
            
            {/* Shared number extensions list */}
            {numberTypeSelection === 'shared' && !availableForAll && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Extensions currently using this shared number:
                </Typography>
                <Box sx={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1, 
                  p: 2,
                  maxHeight: 200,
                  overflow: 'auto',
                  bgcolor: '#f5f5f5'
                }}>
                  {extensions.map((ext) => {
                    const extUser = extensionUsers[ext.MyExten];
                    const hasUser = extUser?.has_user || false;
                    const isChecked = hasUser && (availableForAll || sharedExtensions.includes(ext.MyExten));
                    const isDisabled = !hasUser;
                    
                    if (ext.MyExten === '1000' || ext.MyExten === '1001') {
                      logger.debug(`Debug ${ext.MyExten}:`, {
                        extUser,
                        hasUser,
                        sharedExtensions,
                        isChecked,
                        includesCheck: sharedExtensions.includes(ext.MyExten)
                      });
                    }
                    
                    return (
                      <FormControlLabel
                        key={ext.MyExten}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => handleSharedExtensionToggle(ext.MyExten, e.target.checked)}
                            size="small"
                            disabled={isDisabled}
                          />
                        }
                        label={
                          <span style={{ color: isDisabled ? '#999' : 'inherit' }}>
                            {ext.MyExten}{ext.fullname ? ` - ${ext.fullname}` : ''}
                            {!hasUser && ' (No user)'}
                          </span>
                        }
                        sx={{ display: 'block', mb: 0.5 }}
                      />
                    );
                  })}
                </Box>
                {Object.values(extensionUsers).some(u => !u.has_user) && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Extensions marked "(No user)" cannot use shared numbers as they don't have an associated user account.
                  </Typography>
                )}
              </Box>
            )}
            
            {formData.shared && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Shared numbers can be used by multiple users for outgoing calls.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNumberTypeDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleNumberTypeChange} 
            variant="contained" 
            color="primary"
            disabled={numberTypeSelection === 'direct' && selectedExtension !== '' && isPilotNumber}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for deleting shared number */}
      <Dialog open={confirmDeleteShared} onClose={() => setConfirmDeleteShared(false)}>
        <DialogTitle>Remove Shared Number</DialogTitle>
        <DialogContent>
          <Typography>
            This number is currently shared. Converting it to a direct number will remove it from all users who currently have access to it as a shared number. Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteShared(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              if (numberTypeSelection === 'direct' && selectedExtension) {
                setFormData(prev => ({ ...prev, shared: false, exten: selectedExtension }));
              } else {
                setFormData(prev => ({ ...prev, shared: false, exten: '' }));
              }
              setConfirmDeleteShared(false);
              setNumberTypeDialog(false);
            }} 
            color="error" 
            variant="contained"
          >
            Remove Shared Access
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for removing "available for all" */}
      <Dialog open={confirmNotForAll} onClose={() => setConfirmNotForAll(false)}>
        <DialogTitle>Remove "Available for All"</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to make this number not available for all extensions? You will need to manually select which extensions can use this shared number.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmNotForAll(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setAvailableForAll(false);
              setSharedExtensions(sharedExtensions.filter(ex => ex !== pendingExtension));
              setConfirmNotForAll(false);
            }} 
            color="warning" 
            variant="contained"
          >
            Yes, Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for making "available for all" */}
      <Dialog open={confirmMakeForAll} onClose={() => setConfirmMakeForAll(false)}>
        <DialogTitle>Make Available for All</DialogTitle>
        <DialogContent>
          <Typography>
            All extensions are now selected. Would you like to make this shared number available for all extensions?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMakeForAll(false)}>No</Button>
          <Button 
            onClick={() => {
              setAvailableForAll(true);
              setConfirmMakeForAll(false);
            }} 
            color="primary" 
            variant="contained"
          >
            Yes, Make Available for All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Charge Preview Dialog */}
      <ChargePreviewDialog
        open={chargePreviewOpen}
        onClose={handleChargePreviewClose}
        onConfirm={handleChargePreviewConfirm}
        onAddFunds={handleAddFunds}
        chargeDetails={chargeDetails}
        loading={chargePreviewLoading}
        error={chargePreviewError}
      />

      {/* Cancellation Info Dialog */}
      <Dialog
        open={cancellationInfoDialog.open}
        onClose={() => setCancellationInfoDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <ScheduleIcon sx={{ mr: 1, color: 'warning.main' }} />
            Phone Number Cancellation Scheduled
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Important:</strong> The phone number <strong>{cancellationInfoDialog.phoneNumber}</strong> has been scheduled for cancellation.
            </Typography>
            <Typography variant="body2">
              The number will remain active and usable until 11:59 PM on <strong>{cancellationInfoDialog.cancellationDate}</strong>.
            </Typography>
          </Alert>
          <Typography variant="body2" color="textSecondary">
            • You can continue to make and receive calls until the cancellation date
            <br />
            • The number will be automatically removed after the cancellation date
            <br />
            • No refunds or credits will be issued for phone numbers
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancellationInfoDialog({ open: false })}
            color="primary"
            variant="contained"
          >
            I Understand
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}